/**
 * Stereo UI — keeps the viewer's 2D interface on screen in the split stereo formats.
 *
 * A 3D TV in side-by-side mode shows the left half of the frame to the left eye and the right
 * half to the right eye, so anything drawn once is seen by one eye only. The interface therefore
 * has to exist once per eye, laid out exactly like the eye images:
 *
 *   - The LIVE interface (every container child except the canvas) moves into a left-eye
 *     wrapper that is squeezed into the left eye's region with a CSS transform. It stays fully
 *     interactive: browsers hit-test transformed elements where they are drawn. Elements the
 *     viewer adds to the container later (popups, toasts) are moved in as they arrive.
 *   - A MIRROR of that subtree fills the right eye's region. It is a deep clone kept in sync
 *     incrementally from MutationObserver records, plus a per-frame pass for state the DOM does
 *     not report: form values, scroll positions, and the pixels of canvases and videos (videos
 *     are mirrored onto canvases so media never plays twice). Pointer presses on the mirror are
 *     re-dispatched on the matching live element, and wheel scrolls the live scroll container.
 *
 * Because each wrapper applies the same transform as its eye image, anything the viewer places
 * at a screen position computed from the mono camera (labels, leader lines, popups) lands on
 * the same spot of both eye images — at the depth of the screen surface.
 *
 * Embedded documents (iframes, objects) and audio can't be duplicated without loading or
 * playing them twice, so the mirror shows an empty box of the same size in their place.
 */

import type { SplitStereoFormat as SplitFormat } from './stereoMath';

/** CSS transforms that put a full-size layout into each eye's region (see stereoMath layouts). */
const EYE_TRANSFORMS: Record<SplitFormat, [string, string]> = {
    sbs: ['scale(0.5, 1)', 'translate(50%, 0) scale(0.5, 1)'],
    tb: ['scale(1, 0.5)', 'translate(0, 50%) scale(1, 0.5)'],
    'sbs-full': ['translate(0, 25%) scale(0.5)', 'translate(50%, 25%) scale(0.5)'],
};

/** Offset (fractions of the container) from a point in the right eye to the same point in the left. */
const EYE_OFFSETS: Record<SplitFormat, [number, number]> = {
    sbs: [0.5, 0],
    tb: [0, 0.5],
    'sbs-full': [0.5, 0],
};

const PLACEHOLDER_TAGS = new Set(['IFRAME', 'OBJECT', 'EMBED', 'AUDIO']);
const FORM_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
/** Attributes the mirror must not carry: radio groups and forms are document-wide. */
const STRIPPED_ATTRIBUTES = ['name', 'form', 'autofocus'];
const FORWARDED_POINTER_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'dblclick'] as const;

export const STEREO_EYE_CLASS = 'storysplat-stereo-eye';

export interface StereoUiHandle {
    setFormat(format: SplitFormat): void;
    /** Moves the interface back where it was and removes the mirror. Idempotent. */
    destroy(): void;
}

type FormElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function createStereoUi(container: HTMLElement, canvas: HTMLElement, initialFormat: SplitFormat): StereoUiHandle {
    const doc = container.ownerDocument;
    let format = initialFormat;
    let destroyed = false;

    const liveEye = doc.createElement('div');
    liveEye.className = `${STEREO_EYE_CLASS} ${STEREO_EYE_CLASS}--live`;
    const mirrorEye = doc.createElement('div');
    mirrorEye.className = `${STEREO_EYE_CLASS} ${STEREO_EYE_CLASS}--mirror`;
    mirrorEye.setAttribute('aria-hidden', 'true');

    const toMirror = new WeakMap<Node, Node>();
    const toLive = new WeakMap<Node, Node>();
    const formPairs = new Map<FormElement, FormElement>();
    const canvasPairs = new Map<HTMLCanvasElement | HTMLVideoElement, HTMLCanvasElement>();

    const register = (live: Node, mirror: Node) => {
        toMirror.set(live, mirror);
        toLive.set(mirror, live);
        if (live instanceof HTMLElement && FORM_TAGS.has(live.tagName) && mirror instanceof HTMLElement && FORM_TAGS.has(mirror.tagName)) {
            formPairs.set(live as FormElement, mirror as FormElement);
        } else if ((live instanceof HTMLCanvasElement || live instanceof HTMLVideoElement) && mirror instanceof HTMLCanvasElement) {
            canvasPairs.set(live, mirror);
        }
    };

    /** A stand-in element carrying the original's attributes (class, style) but no content. */
    const standIn = (live: Element, tag: string): HTMLElement => {
        const el = doc.createElement(tag);
        for (const attr of Array.from(live.attributes)) {
            if (attr.name === 'src' || attr.name === 'srcdoc' || attr.name === 'data') continue;
            try { el.setAttribute(attr.name, attr.value); } catch { /* attribute not valid here */ }
        }
        return el;
    };

    /** Deep-clone a live node for the mirror, neutralising anything that must not run twice. */
    const cloneForMirror = (live: Node): Node => {
        if (live instanceof HTMLVideoElement) {
            const c = standIn(live, 'canvas');
            register(live, c);
            return c;
        }
        if (live instanceof Element && PLACEHOLDER_TAGS.has(live.tagName)) {
            const c = standIn(live, 'div');
            register(live, c);
            return c;
        }
        const mirror = live.cloneNode(false);
        if (mirror instanceof Element) {
            for (const name of STRIPPED_ATTRIBUTES) mirror.removeAttribute(name);
        }
        register(live, mirror);
        for (const child of Array.from(live.childNodes)) mirror.appendChild(cloneForMirror(child));
        return mirror;
    };

    /** Make a mirror node's children match its live node's, reusing mirrors that already exist. */
    const syncChildren = (live: Node) => {
        const mirror = toMirror.get(live);
        if (!mirror) return;
        const wanted = Array.from(live.childNodes).map(child => {
            const existing = toMirror.get(child);
            return existing && existing.parentNode === mirror ? existing : cloneForMirror(child);
        });
        const current = Array.from(mirror.childNodes);
        if (wanted.length === current.length && wanted.every((node, i) => node === current[i])) return;
        for (const node of current) if (!wanted.includes(node)) mirror.removeChild(node);
        wanted.forEach((node, i) => {
            if (mirror.childNodes[i] !== node) mirror.insertBefore(node, mirror.childNodes[i] ?? null);
        });
    };

    const applyRecords = (records: MutationRecord[]) => {
        const childTargets = new Set<Node>();
        for (const record of records) {
            if (record.type === 'childList') {
                childTargets.add(record.target);
            } else if (record.type === 'characterData') {
                const mirror = toMirror.get(record.target);
                if (mirror) mirror.nodeValue = record.target.nodeValue;
            } else if (record.type === 'attributes') {
                // The wrappers carry their own (different) transforms.
                if (record.target === liveEye) continue;
                const live = record.target as Element;
                const mirror = toMirror.get(live);
                const name = record.attributeName;
                if (!(mirror instanceof Element) || !name || STRIPPED_ATTRIBUTES.includes(name)) continue;
                // Stand-ins (video canvases, embed boxes) must never pick up a source.
                if (mirror.tagName !== live.tagName && (name === 'src' || name === 'srcdoc' || name === 'data')) continue;
                const ns = record.attributeNamespace;
                const value = ns ? live.getAttributeNS(ns, name.split(':').pop() ?? name) : live.getAttribute(name);
                try {
                    if (value === null) {
                        if (ns) mirror.removeAttributeNS(ns, name.split(':').pop() ?? name);
                        else mirror.removeAttribute(name);
                    } else if (ns) {
                        mirror.setAttributeNS(ns, name, value);
                    } else {
                        mirror.setAttribute(name, value);
                    }
                } catch { /* attribute not valid on the stand-in */ }
            }
        }
        childTargets.forEach(syncChildren);
    };

    const observer = new MutationObserver(applyRecords);

    // ── Live side: the viewer keeps appending to the container; adopt new arrivals ──
    const adopt = (node: Node) => {
        if (node === canvas || node === liveEye || node === mirrorEye) return;
        liveEye.appendChild(node);
    };
    const containerObserver = new MutationObserver((records) => {
        for (const record of records) record.addedNodes.forEach(node => { if (node.parentNode === container) adopt(node); });
    });

    // Remember where everything was so destroy() can put it back on the same side of the canvas.
    const originalChildren = Array.from(container.childNodes);
    const canvasIndex = originalChildren.indexOf(canvas);
    const beforeCanvas = canvasIndex > 0 ? originalChildren.slice(0, canvasIndex) : [];
    const afterCanvas = originalChildren.slice(canvasIndex + 1);
    [...beforeCanvas, ...afterCanvas].forEach(n => liveEye.appendChild(n));
    container.appendChild(liveEye);
    // Built once up front; afterwards only changes are applied.
    register(liveEye, mirrorEye);
    for (const child of Array.from(liveEye.childNodes)) mirrorEye.appendChild(cloneForMirror(child));
    container.appendChild(mirrorEye);

    observer.observe(liveEye, { subtree: true, childList: true, attributes: true, characterData: true, attributeOldValue: false });
    containerObserver.observe(container, { childList: true });

    // ── Per-frame state the DOM doesn't report ──
    const syncScroll = (live: Element) => {
        const mirror = toMirror.get(live);
        if (mirror instanceof Element) {
            mirror.scrollTop = live.scrollTop;
            mirror.scrollLeft = live.scrollLeft;
        }
    };
    const onLiveScroll = (event: Event) => {
        if (event.target instanceof Element) syncScroll(event.target);
    };
    liveEye.addEventListener('scroll', onLiveScroll, true);
    liveEye.querySelectorAll('*').forEach(el => { if (el.scrollTop || el.scrollLeft) syncScroll(el); });

    let frame = 0;
    const tick = () => {
        if (destroyed) return;
        formPairs.forEach((mirror, live) => {
            if (!live.isConnected) { formPairs.delete(live); return; }
            if (mirror.value !== live.value) mirror.value = live.value;
            if (live instanceof HTMLInputElement && mirror instanceof HTMLInputElement && mirror.checked !== live.checked) {
                mirror.checked = live.checked;
            }
        });
        canvasPairs.forEach((mirror, live) => {
            if (!live.isConnected) { canvasPairs.delete(live); return; }
            const isVideo = live instanceof HTMLVideoElement;
            const width = isVideo ? live.videoWidth : live.width;
            const height = isVideo ? live.videoHeight : live.height;
            if (!width || !height || (isVideo && live.readyState < 2)) return;
            if (!isVideo && live.getClientRects().length === 0) return; // hidden: nothing to copy
            if (mirror.width !== width) mirror.width = width;
            if (mirror.height !== height) mirror.height = height;
            const ctx = mirror.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, width, height);
            try { ctx.drawImage(live, 0, 0, width, height); } catch { /* not drawable yet */ }
        });
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    // ── Mirror input: presses act on the live element they mirror ──
    const liveFor = (target: EventTarget | null): Element | null => {
        let node = target instanceof Node ? target : null;
        while (node && node !== mirrorEye) {
            const live = toLive.get(node);
            if (live instanceof Element) return live;
            node = node.parentNode;
        }
        return null;
    };
    const eyeOffsetPx = (): [number, number] => {
        const rect = container.getBoundingClientRect();
        const [fx, fy] = EYE_OFFSETS[format];
        return [fx * rect.width, fy * rect.height];
    };
    const forwardPointer = (event: Event) => {
        const live = liveFor(event.target);
        if (!live || !(event instanceof MouseEvent)) return;
        event.stopPropagation();
        event.preventDefault();
        const [dx, dy] = eyeOffsetPx();
        const init: PointerEventInit = {
            bubbles: true,
            cancelable: true,
            composed: true,
            clientX: event.clientX - dx,
            clientY: event.clientY - dy,
            screenX: event.screenX - dx,
            screenY: event.screenY - dy,
            button: event.button,
            buttons: event.buttons,
            detail: event.detail,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            metaKey: event.metaKey,
        };
        if (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent) {
            live.dispatchEvent(new PointerEvent(event.type, { ...init, pointerId: event.pointerId, pointerType: event.pointerType, isPrimary: event.isPrimary }));
        } else {
            live.dispatchEvent(new MouseEvent(event.type, init));
        }
        if (event.type === 'pointerdown' && live instanceof HTMLElement && FORM_TAGS.has(live.tagName)) {
            live.focus({ preventScroll: true });
        }
    };
    const forwardWheel = (event: WheelEvent) => {
        let live = liveFor(event.target);
        if (!live) return;
        event.stopPropagation();
        while (live && live !== liveEye) {
            const style = getComputedStyle(live);
            const scrollsY = /auto|scroll/.test(style.overflowY) && live.scrollHeight > live.clientHeight;
            const scrollsX = /auto|scroll/.test(style.overflowX) && live.scrollWidth > live.clientWidth;
            if (scrollsY || scrollsX) {
                live.scrollBy(event.deltaX, event.deltaY);
                event.preventDefault();
                return;
            }
            live = live.parentElement;
        }
    };
    // Capture phase: the press is handled before it reaches any mirror element, so a listener
    // the viewer attached to every match of a selector (mirror copies included) never runs twice.
    for (const type of FORWARDED_POINTER_EVENTS) mirrorEye.addEventListener(type, forwardPointer, true);
    mirrorEye.addEventListener('wheel', forwardWheel, { capture: true, passive: false });

    const applyFormat = () => {
        const [live, mirror] = EYE_TRANSFORMS[format];
        liveEye.style.transform = live;
        mirrorEye.style.transform = mirror;
    };
    applyFormat();

    return {
        setFormat(next: SplitFormat) {
            if (destroyed || next === format) return;
            format = next;
            applyFormat();
        },
        destroy() {
            if (destroyed) return;
            destroyed = true;
            cancelAnimationFrame(frame);
            observer.disconnect();
            containerObserver.disconnect();
            liveEye.removeEventListener('scroll', onLiveScroll, true);
            // Everything goes back to the container in its original order around the canvas,
            // followed by anything the viewer added while stereo was on.
            const canvasHere = canvas.parentNode === container;
            for (const node of beforeCanvas) {
                if (node.parentNode === liveEye) container.insertBefore(node, canvasHere ? canvas : liveEye);
            }
            for (const node of afterCanvas) {
                if (node.parentNode === liveEye) container.insertBefore(node, liveEye);
            }
            for (const node of Array.from(liveEye.childNodes)) container.insertBefore(node, liveEye);
            liveEye.remove();
            mirrorEye.remove();
            formPairs.clear();
            canvasPairs.clear();
        },
    };
}
